import React, { useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Button, Grid, IconButton } from "@mui/material";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";

import BaseTable from "Components/Common/BaseTable";
import BreadCrumb from "Components/Common/BreadCrumb";
import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";
import {
  getOptionLabel,
  subscriptionTypes,
} from "common/enums";
import Tooltip from "Components/Common/Tooltip";
import SelectField from "Components/Common/SelectField";
import InputFieldText from "Components/Common/InputFieldText";

import { MY_BILLS } from "tagTypes";

import { getImageUrl, isAuthorizedUser } from "utils";

const RECORDS_PER_PAGE = 10;
const BACKUP_POWER = 3;

const initialQuery = { consumerNumber: "", utilityId: "" };

const MyBills = ({ t }) => {
  const [documentToPreview, setDocumentToPreview] = useState();
  const [subscriptionType, setSubscriptionType] = useState("");
  const [consumerNumber, setConsumerNumber] = useState("");
  const [query, setQuery] = useState(initialQuery);
  const { roles } = useSelector((state) => state.Login);

  const updatedBillingSubscriptionTypes = useMemo(
    () =>
      subscriptionTypes?.filter(({ allowedRoles }) =>
        isAuthorizedUser(roles, allowedRoles)
      ),
    [roles, subscriptionTypes]
  );

  const serverSidePaginationConfig = useMemo(() => {
    const endpoint =
      subscriptionType === BACKUP_POWER
        ? "/backup-power-bill-calculation/user"
        : "/bill-calculation/user";

    return {
      endpoint,
      tags: [MY_BILLS],
      query,
    };
  }, [query]);

  const columns = [
    {
      title: "Consumer Number",
      field: "consumerNumber",
    },
    {
      title: "Utility",
      renderCell: ({ utilityId }) =>
        getOptionLabel("utilityOptions", utilityId),
    },
    {
      title: "Bill Cycle",
      field: "billCycle",
    },
    { title: "Amount", field: "currentMonthBill" },
    { title: "Net Payable", field: "netPayable" },
    {
      title: "Status",
      renderCell: ({ status }) =>
        getOptionLabel("billCalculationStatus", status),
    },
    {
      title: "Actions",
      renderCell: ({ url, accountNumber }) => (
        <IconButton
          onClick={() =>
            setDocumentToPreview({
              url: url ? getImageUrl(url) : "",
              documentTypeName: "Bill",
              documentType: "pdf",
              documentName: `${accountNumber}-bill`,
            })
          }
        >
          <Tooltip title={t("View Bill")}>
            <RemoveRedEyeIcon fontSize="small" color="primary" />
          </Tooltip>
        </IconButton>
      ),
    },
  ];

  return (
    <div className="page-content">
      {!!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => setDocumentToPreview(null)}
          {...documentToPreview}
        />
      )}
      <BreadCrumb title={t("My Bills")} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setQuery({ consumerNumber, subscriptionType });
        }}
      >
        <Grid container columnSpacing={2} className="mb-1">
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <SelectField
              label={t("Subscription Type") + "*"}
              onChange={(_, { value }) => {
                setSubscriptionType(value);
              }}
              value={
                updatedBillingSubscriptionTypes?.find(
                  ({ value }) => value === subscriptionType
                ) ?? ""
              }
              options={updatedBillingSubscriptionTypes}
            />
          </Grid>
          <Grid item lg={3} md={4} sm={6} xs={12}>
            <InputFieldText
              label={t("Consumer Number")}
              placeholder={t("Enter consumer number")}
              type="number"
              value={consumerNumber}
              onChange={(e) => setConsumerNumber(e.target.value)}
            />
          </Grid>
          <Grid item>
            <Button variant="outlined" className="mb-2 me-2" type="submit">
              {t("Search")}
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setQuery(initialQuery);
                setConsumerNumber("");
                setSubscriptionType("");
              }}
              className="mb-2 me-2"
            >
              {t("Clear")}
            </Button>
          </Grid>
        </Grid>
      </form>
      <BaseTable
        columns={columns}
        pageSize={RECORDS_PER_PAGE}
        paginationType="serverSide"
        serverSidePaginationConfig={serverSidePaginationConfig}
        maxHeight="calc(100vh - 185px)"
      />
    </div>
  );
};

MyBills.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(MyBills);
